import React from "react";
import { ActionItemsCardProps } from "./ActionItemsCard.types";

import {
  Body5MediumTypography,
  Body5RegularTypography,
} from "../../../primitives/Typography/typographyVariants";
import { colorPalette } from "../../../theme/colors";
import { Icon } from "../../../components/Icon/Icon";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import Box from "../../../primitives/Box/Box";
import Tags from "../../../components/Tags/Tags";

export const ActionItemsCard: React.FC<ActionItemsCardProps> = ({
  title,
  subtitle,
  variant = "enabled",
  tagText,
  tagIcon,
  tagTextColor,
  tagBackgroundColor,
  tagIconColor,
  onClick,
  dataCy = "action-items-card",
  showArrow = true,
  showNotificationDot = false,
  cardIcon,
  helpText,
}) => {
  const isDisabled = variant === "disabled";

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <FlexRow
      px={12}
      py={6}
      justifyContent="space-between"
      alignItems="center"
      borderRadius={8}
      border={`1px solid ${colorPalette.popWhite[200]}`}
      cursor={isDisabled ? "default" : "pointer"}
      onClick={handleClick}
      data-cy={dataCy}
      height="48px"
      backgroundColor={colorPalette.popWhite[500]}
      position="relative"
    >
      <FlexRow
        gap={8}
        alignItems="center"
        width="unset"
        opacity={isDisabled ? 0.5 : 1}
      >
        <Icon
          name={cardIcon || "fa-solid fa-link"}
          color={colorPalette.popBlack[500]}
          size="16"
          height="20"
          width="20"
          dataCy={`${dataCy}-card-icon`}
        />
        <FlexColumn>
          <Body5MediumTypography
            color={colorPalette.popBlack[500]}
            data-cy={`${dataCy}-title`}
          >
            {title}
          </Body5MediumTypography>
          <Body5RegularTypography
            color={colorPalette.popBlack[500]}
            data-cy={`${dataCy}-subtitle`}
          >
            {subtitle}
          </Body5RegularTypography>
        </FlexColumn>
      </FlexRow>
      <FlexRow
        gap={4}
        alignItems="center"
        width="unset"
        opacity={isDisabled ? 0.5 : 1}
      >
        {helpText ? (
          <Body5RegularTypography
            color={colorPalette.popBlack[100]}
            data-cy={`${dataCy}-help-text`}
          >
            {helpText}
          </Body5RegularTypography>
        ) : (
          tagText && (
            <Tags
              variant="primary"
              label={tagText}
              color={tagTextColor}
              backgroundColor={tagBackgroundColor}
              {...(tagIcon && { icon: tagIcon })}
              {...(tagIconColor && { iconColor: tagIconColor })}
            />
          )
        )}
        {showArrow && (
          <Icon
            name="fa-solid fa-chevron-right"
            size="12"
            height="18"
            width="18"
            color={colorPalette.popBlack[100]}
            data-cy={`${dataCy}-arrow`}
            cursor={isDisabled ? "default" : "pointer"}
          />
        )}
      </FlexRow>
      {showNotificationDot && (
        <Box
          height="12px"
          width="12px"
          backgroundColor={colorPalette.popOrange[500]}
          border={`1px solid ${colorPalette.popWhite[500]}`}
          borderRadius="50%"
          position="absolute"
          top="-4px"
          right="-4px"
          data-cy={`${dataCy}-notification-dot`}
          px={4}
          py={5}
          zIndex={1}
        />
      )}
    </FlexRow>
  );
};
